
const { registerBlockType } = wp.blocks;
const { store: noticesStore } = wp.notices;
const { useDispatch, useSelect } = wp.data;
const { InspectorControls, useBlockProps } = wp.blockEditor;
const { PanelBody, SelectControl } = wp.components;
const { useState, useEffect, Component } = wp.element;
const apiFetch = wp.apiFetch;

/**
 * Localized data from PHP is contained in SareData global variable
 * @var SareData
 * @property SareData.sites
 * @property SareData.currentSiteUrl
 * @property SareData.nonce
 */

// Set the nonce we will use for API calls
apiFetch.use( apiFetch.createNonceMiddleware( SareData.nonce ) );

export default registerBlockType(
	"sare/gms-impact",
	{
		title : "GMS Impact",
		description : "Displays an impact fact directly from the GMS",
		category : "sare-blocks",
		icon: "chart-bar",
		keywords: [
			"impacts",
			"grants"
		],
		attributes: {
			impact: {
				type: "string",
				default: "",
				required: true,
			},
			location: {
				type: "string",
				default: "",
			},
			locationLabel: {
				type: "string",
				default: "",
			},
			type: {
				type: "string",
				default: "",
			},
			description: {
				type: "string",
				default: "",
			},
		},

		// Add validation function
		validate: ({ impact }) => {
			if (!impact) {
				return {
					isValid: false,
					message: 'Impact filed is required.',
				};
			}
			return { isValid: true };
		},

		edit: ({ attributes, setAttributes, clientId }) => {

			const { createWarningNotice, removeNotice } = useDispatch(noticesStore);

			// State to store the project types
			const [projectTypes, setProjectTypes] = useState([]);
			const [impact, setImpact] = useState(0);

			// Check for required fields
			const hasRequiredFields = attributes.impact;
			const noticeId = `gms-impact-required-fields-${clientId}`;

			// Fetch project types after the component mounts
			useEffect(() => {
				async function getProjectTypes() {

					// Perform the AJAX call
					let jsonResponse = await fetch("https://projects.sare.org/wp-json/sare/v2/project/types/", {
						method: "GET",
						headers: {
							"Content-Type": "application/json",
						},
					}).then( async (response) => {
						// Process the JSON data as needed

						let json = await response.json();

						// Convert to an array
						let data = Object.entries(json);

						// Format the JSON for key and value
						return data.map(([key, value]) => ({
								label: value,
								value: value
							})
						);

					}).catch((error) => {
						throw new Error(error);

						// fallback value
						return [];
					});

					// Set the data to the state
					setProjectTypes([{ label: "All Types", value: "" }, ...jsonResponse]
					);

				}

				async function getImpact(attributes) {

                    // Get the current year and calculate the range for the past 5 years
                    const endYear = new Date().getFullYear();
                    const startYear = endYear - 5;

                    // Build the query parameters dynamically
                    const params = new URLSearchParams({
                        start_year: startYear,
                        end_year: endYear,
                    });

                    if (attributes.location) {
                        params.append("geographic_scope", attributes.location);
                    }

                    if (attributes.type) {
                        params.append("project_type", attributes.type);
                    }

                    // Base URL for the API
                    const baseUrl = "https://projects.sare.org/wp-json/sare/v2/projects-impacts/";

                    // Combine the base URL with the query string
                    const url = `${baseUrl}?${params.toString()}`;

                    // Perform the AJAX call
                    let jsonResponse = await fetch(url, {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                        },
                    })
                        .then((response) => {
                            // Return or process the JSON data as needed
                            return response.json();
                        })
                        .catch((error) => {
                            console.error("Error:", error);

                            // fallback value
                            return [];
                        });

					let impactNumber = jsonResponse.impacts[attributes.impact];

					// Convert to a number and format the number with commas
					impactNumber = Number(impactNumber);

					if (isNaN(impactNumber)) {
						impactNumber = "#";
					} else {
						impactNumber = impactNumber.toLocaleString();
					}

					setImpact(impactNumber);

				}

				// Initially call the async functions
				getProjectTypes();
				getImpact(attributes);

				if (!hasRequiredFields) {
					createWarningNotice(
						"Impact field is required. This block will not be saved until this field has a value.",
						{
							id: noticeId,
							isDismissible: false,
							type: 'error',
						}
					);
				} else {
					removeNotice(noticeId);
				}

				// Clean up the notice when component unmounts
				return () => {
					removeNotice(noticeId);
				};


			}, [attributes,hasRequiredFields, clientId]);


			/**
			 * Updates the attributes of the block when a select dropdown is changed in the edit panel
			 *
			 * @param field
			 * @returns {(function(*): void)|*}
			 */
			const updateSelect = (field) => (value) => {
				setAttributes({ [field]: value });
			};

			// Set the description based on the impact
			attributes.description = getImpactDescription(attributes.impact);
			setAttributes({['description'] : attributes.description});

			// Set the location label based on the abbreviation
			attributes.locationLabel = getLocationLabel(attributes.location);
			setAttributes({['locationLabel'] : attributes.locationLabel});

			return (
				<>
					{/* Add the select field to the block's edit panel */}
					<InspectorControls>
						<PanelBody title="Settings" initialOpen={true}>
							{!hasRequiredFields && (
								<div style={{ color: 'red', marginBottom: '10px' }}>
									Please fill in all required fields marked with *
								</div>
							)}
							<SelectControl
								label="Impact *"
								value={attributes.impact}
								options={[
									{ label: "-- Select One --", value: "" },
									{ label: "Educational Activities Number Farmers Participating", value: "num_farmers_participating" },
									{ label: "Farmers Reporting KASA", value: "num_farmers_reporting_kasa" },
									{ label: "Farmers Changed", value: "num_farmers_change" },
								]}
								onChange={updateSelect("impact")}
								help={!attributes.impact ? "This field is required" : ""}
							/>
							<SelectControl
								label="Location"
								value={attributes.location}
								options={getLocationValues()}
								onChange={updateSelect("location")}
							/>
							<SelectControl
								label="Project Type *"
								value={attributes.type}
								options={projectTypes}
								onChange={updateSelect("type")}
							/>
						</PanelBody>
					</InspectorControls>
					{/* Main block editing UI */}

					<div { ...useBlockProps({
						className: !hasRequiredFields ? 'has-error' : '',
					}) } >
						<div className="wp-block-sare-gms-impact">
                    <span className="inner">
                        <h4 className="heading cf">{attributes.locationLabel} {attributes.type || ""} Grants</h4>
                        <p className="cf">
                            <span className="impact">{impact}</span>
                            <span className="description">{attributes.description}</span>
                        </p>
                        <small className="disclaimer">{getDisclaimerDate()}</small>
                    </span>
						</div>
					</div>

				</>
			);
		},

		/**
		 * We save nothing to the database as we render dynamic content in PHP class::render()
		 *
		 * @returns {null}
		 */
		save: () => {
			return null;
		}
	});

function getDisclaimerDate() {
	// Get the current year and calculate the range for the past 5 years
	const endYear = new Date().getFullYear();
	const startYear = endYear - 5;

	return "From "+startYear+" to "+endYear+" grantee reports.";
}

/**
 * Grab the label for the given location abbreviation
 *
 * @param location
 * @returns {string}
 */
function getLocationLabel(abbreviation) {

	let locations = getLocationValues();
	let label = "";

	for (const loc of locations) {
		if (loc.value === abbreviation) {
			label = loc.label;
			break;
		}
	}

	return label;
}

function getLocationValues() {

	return [
		{ label: "SARE", value: "" },
		{ label: "North Central SARE", value: "north central" },
		{ label: "Northeast SARE", value: "northeast" },
		{ label: "Southern SARE", value: "southern" },
		{ label: "Western SARE", value: "western" },
		{ label: "----", value: "" },
		{ label: "Alabama", value: "al" },
		{ label: "Alaska", value: "ak" },
		{ label: "American Samoa", value: "as" },
		{ label: "Arizona", value: "az" },
		{ label: "Arkansas", value: "ar" },
		{ label: "California", value: "ca" },
		{ label: "Colorado", value: "co" },
		{ label: "Connecticut", value: "ct" },
		{ label: "Delaware", value: "de" },
		{ label: "District of Columbia", value: "dc" },
		{ label: "Florida", value: "fl" },
		{ label: "Georgia", value: "ga" },
		{ label: "Guam", value: "gu" },
		{ label: "Hawaii", value: "hi" },
		{ label: "Idaho", value: "id" },
		{ label: "Illinois", value: "il" },
		{ label: "Indiana", value: "in" },
		{ label: "Iowa", value: "ia" },
		{ label: "Kansas", value: "ks" },
		{ label: "Kentucky", value: "ky" },
		{ label: "Louisiana", value: "la" },
		{ label: "Maine", value: "me" },
		{ label: "Maryland", value: "md" },
		{ label: "Massachusetts", value: "ma" },
		{ label: "Michigan", value: "mi" },
		{ label: "Minnesota", value: "mn" },
		{ label: "Mississippi", value: "ms" },
		{ label: "Missouri", value: "mo" },
		{ label: "Montana", value: "mt" },
		{ label: "Nebraska", value: "ne" },
		{ label: "Nevada", value: "nv" },
		{ label: "New Hampshire", value: "nh" },
		{ label: "New Jersey", value: "nj" },
		{ label: "New Mexico", value: "nm" },
		{ label: "New York", value: "ny" },
		{ label: "North Carolina", value: "nc" },
		{ label: "North Dakota", value: "nd" },
		{ label: "Northern Mariana Islands", value: "mp" },
		{ label: "Ohio", value: "oh" },
		{ label: "Oklahoma", value: "ok" },
		{ label: "Oregon", value: "or" },
		{ label: "Pennsylvania", value: "pa" },
		{ label: "Puerto Rico", value: "pr" },
		{ label: "Rhode Island", value: "ri" },
		{ label: "South Carolina", value: "sc" },
		{ label: "South Dakota", value: "sd" },
		{ label: "Tennessee", value: "tn" },
		{ label: "Texas", value: "tx" },
		{ label: "Utah", value: "ut" },
		{ label: "Vermont", value: "vt" },
		{ label: "Virginia", value: "va" },
		{ label: "Virgin Islands", value: "vi" },
		{ label: "Washington", value: "wa" },
		{ label: "West Virginia", value: "wv" },
		{ label: "Wisconsin", value: "wi" },
		{ label: "Wyoming", value: "wy" }
	];

}

function getImpactDescription(impact) {

	let description = "";

	switch (impact) {
		case "num_farmers_participating":
			description = "Number of farmers and ranchers participating in projects";
			break;
		case "num_farmers_reporting_kasa":
			description = "Number of farmers and ranchers that learned";
			break;
		case "num_farmers_change":
			description = "Number of farmers and ranchers changed a farming practice";
			break;
		default:
			description = "Please select an impact to get started.";
	}

	return description;
}